import { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetChildQuery, useGetChildSiblingGroupQuery } from '@/services/child-api';
import { selectorPermissions } from '@/stores/auth';
import {
  CalendarAltSvg,
  FileAltSvg,
  FileSignatureSvg,
  HistorySvg,
  SearchSvg,
  StreamSvg,
  TasksSvg,
  UsersSvg,
} from '@/assets/icons';
import Page from '@/components/page';
import PageTitle from '@/components/page-title';
import Panel from '@/components/panel';
import ChildInfo from './child-info';
import Inquiries from './inquiries';
import SiblingGroup from './sibling-group';
import Events from './events';
import Documents from './documents';
import RecruitmentRequests from './recruitment-requests';
import Activity from './activity';
import TaskList from './task-list';
import AdoptionHistory from './adoption-history';
import PlacementHistory from './placement-history';
import { useGetGroupMembersQuery } from '@/services/sibling-group-api';

const breadcrumb = [{ path: '/child', label: 'Children' }, 'View'];

const titles = {
  inquiries: 'Child Inquiries',
  'sibling-group': 'Siblings Group',
  events: 'Child Events',
  documents: 'Child Documents',
  'recruitment-requests': 'Recruitment Requests',
  activity: 'Child Activity',
  'task-list': 'Child Tasks',
  placements: 'Placement History',
  adoptions: 'Adoption History',
};

const tabData = [
  { key: 'inquiries', icon: SearchSvg, label: 'Inquiries', component: Inquiries },
  { key: 'sibling-group', icon: UsersSvg, label: 'Siblings', component: SiblingGroup },
  { key: 'events', icon: CalendarAltSvg, label: 'Events', component: Events },
  { key: 'documents', icon: FileAltSvg, label: 'Documents', component: Documents },
  {
    key: 'recruitment-requests',
    icon: FileSignatureSvg,
    label: 'Recruitment Requests',
    component: RecruitmentRequests,
  },
  { key: 'activity', icon: StreamSvg, label: 'Activity', component: Activity },
  { key: 'task-list', icon: TasksSvg, label: 'Task List', component: TaskList },
  { key: 'placements', icon: HistorySvg, label: 'Match/Placement History', component: PlacementHistory },
  { key: 'adoptions', icon: HistorySvg, label: 'Adoption History', component: AdoptionHistory },
];

const ChildViewPage = () => {
  const permissions = useSelector(selectorPermissions);
  const { childId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const activeTabKey = pathname
    .split('/')
    .filter((s) => s)
    .pop();

  const { data } = useGetChildQuery({ childId });
  const { data: groupData, isLoading: loadingGroupData } = useGetChildSiblingGroupQuery({
    childId,
  });
  const { data: members, isFetching: loadingMembers } = useGetGroupMembersQuery(
    {
      groupId: groupData?.siblingGroupId,
      pageNumber: 0,
      excludeChildId: childId,
    },
    { skip: !groupData?.siblingGroupId },
  );

  const tabItems = useMemo(() => {
    if (!data) return [];
    if (data.hasPlacementHistory) return tabData;
    return tabData.filter((item) => item.key !== 'placements');
  }, [data]);

  const siblingsNumber = useMemo(() => {
    if (!members) return 0;
    if (members.count > 0) return members.count;
    return 0;
  }, [members]);

  const tabTitles = useMemo(
    () =>
      tabItems.map(({ key, icon: Icon, label }) => {
        if (key === 'sibling-group') {
          return {
            key,
            label: (
              <>
                <Icon />
                {label}
                {siblingsNumber ? ` (${siblingsNumber})` : ''}
              </>
            ),
          };
        } else {
          return {
            key,
            label: (
              <>
                <Icon />
                {label}
              </>
            ),
          };
        }
      }),
    [tabItems, siblingsNumber],
  );

  return (
    <Page breadcrumb={breadcrumb} size="xl" loading={!data || loadingMembers || loadingGroupData}>
      <PageTitle label={titles[activeTabKey]} />
      <ChildInfo childId={childId} permissions={permissions} />
      <Panel tabList={tabTitles} activeTabKey={activeTabKey} onTabChange={(key) => navigate(key)}>
        <Routes>
          {tabItems.map(({ key, component: Component }) => (
            <Route key={key} path={key} element={<Component childId={childId} permissions={permissions} />} />
          ))}
          <Route path="*?" element={<Navigate to="inquiries" replace />} />
        </Routes>
      </Panel>
    </Page>
  );
};

export default ChildViewPage;
